import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, startOfWeek as sow, endOfWeek as eow,
} from "date-fns";
import { CalendarOff, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { RosterMonthCalendar } from "@/components/roster/RosterMonthCalendar";
import { RosterShiftList } from "@/components/roster/RosterShiftList";
import { RosterShiftForm } from "@/components/roster/RosterShiftForm";
import {
  getStaff, getRoster, getRosterRemovals, createRosterAssignment,
  deleteRosterByDetails, getLeaveRequests, createLeaveRequest,
  updateLeaveRequest, deleteLeaveRequest,
} from "@/lib/api";

export default function Roster() {
  const { branches, selectedBranch } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const branchId = selectedBranch || branches[0]?.id;

  const [staff, setStaff] = useState([]);
  const [roster, setRoster] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Month navigation
  const [monthDate, setMonthDate] = useState(startOfMonth(new Date()));
  const gridStart = useMemo(() => startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 }), [monthDate]);
  const gridEnd = useMemo(() => endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 }), [monthDate]);
  const days = useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart, gridEnd]);

  // Leave list filter (separate tab)
  const [leaveFilter, setLeaveFilter] = useState("this_month");
  const [leaveCustomStart, setLeaveCustomStart] = useState("");
  const [leaveCustomEnd, setLeaveCustomEnd] = useState("");
  const [leaveListRequests, setLeaveListRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);

  // Assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [assignmentNotes, setAssignmentNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Leave request dialog (range)
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    staff_id: "", start_date: new Date(), end_date: new Date(), reason: "",
  });

  // ----- Fetch month roster -----
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const start = format(gridStart, "yyyy-MM-dd");
      const end = format(gridEnd, "yyyy-MM-dd");
      const [staffData, rosterData, removalData, leaveData] = await Promise.all([
        getStaff(),
        getRoster(start, end),
        getRosterRemovals(start, end),
        getLeaveRequests({ start_date: start, end_date: end }),
      ]);
      setStaff(staffData);
      setRoster(rosterData);
      setRemovals(removalData);
      setLeaveRequests(leaveData);
    } catch {
      toast.error("Failed to load roster data");
    } finally {
      setLoading(false);
    }
  }, [gridStart, gridEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ----- Leave list (separate filter) -----
  const getLeaveRange = useCallback(() => {
    const today = new Date();
    switch (leaveFilter) {
      case "this_week":
        return { start: format(sow(today, { weekStartsOn: 1 }), "yyyy-MM-dd"), end: format(eow(today, { weekStartsOn: 1 }), "yyyy-MM-dd") };
      case "this_month":
        return { start: format(startOfMonth(today), "yyyy-MM-dd"), end: format(endOfMonth(today), "yyyy-MM-dd") };
      case "all":
        return { start: null, end: null };
      case "custom":
        return { start: leaveCustomStart || null, end: leaveCustomEnd || null };
      default:
        return { start: null, end: null };
    }
  }, [leaveFilter, leaveCustomStart, leaveCustomEnd]);

  const fetchLeaveList = useCallback(async () => {
    setLeaveLoading(true);
    try {
      const { start, end } = getLeaveRange();
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      setLeaveListRequests(await getLeaveRequests(params));
    } catch {
      toast.error("Failed to load leave requests");
    } finally {
      setLeaveLoading(false);
    }
  }, [getLeaveRange]);

  useEffect(() => { fetchLeaveList(); }, [fetchLeaveList]);

  // ----- Cell helpers (single branch) -----
  const getAssignmentsForCell = useCallback((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dayOfWeek = format(date, "EEEE").toLowerCase();
    const removalKeys = new Set(
      removals.filter((r) => r.date === dateStr && r.branch_id === branchId).map((r) => r.staff_id)
    );
    const manualAssignments = roster.filter((r) => r.date === dateStr && r.branch_id === branchId);
    const manualStaffIds = new Set(manualAssignments.map((a) => a.staff_id));
    const regularAssignments = staff
      .filter((s) => {
        if (manualStaffIds.has(s.id) || removalKeys.has(s.id)) return false;
        return (s.regular_roster || []).some((r) => r.day === dayOfWeek && r.branch_id === branchId);
      })
      .map((s) => ({ staff_id: s.id, staff_name: s.name, branch_id: branchId, date: dateStr, source: "regular" }));
    return [
      ...manualAssignments.map((a) => ({ ...a, source: a.source || "manual" })),
      ...regularAssignments,
    ];
  }, [roster, removals, staff, branchId]);

  // A leave covers a date if the date is within [start_date, end_date]
  const getLeavesForDate = useCallback((date) => {
    const d = format(date, "yyyy-MM-dd");
    return leaveRequests.filter((lr) => {
      const s = lr.start_date || lr.date;
      const e = lr.end_date || lr.date;
      return d >= s && d <= e;
    });
  }, [leaveRequests]);

  // Was the staff regularly scheduled (had an active shift) for this day at this branch?
  const wasScheduled = useCallback((staffId, date) => {
    const dayOfWeek = format(date, "EEEE").toLowerCase();
    const s = staff.find((x) => x.id === staffId);
    if (!s) return false;
    return (s.regular_roster || []).some((r) => r.day === dayOfWeek && r.branch_id === branchId);
  }, [staff, branchId]);

  // Approved leaves shown on the calendar: only on days the staff actually had a shift
  const getActiveLeavesForDate = useCallback((date) => {
    return getLeavesForDate(date).filter(
      (lr) => lr.status === "approved" && wasScheduled(lr.staff_id, date)
    );
  }, [getLeavesForDate, wasScheduled]);

  const isStaffOnLeave = useCallback((staffId, date) => {
    return getLeavesForDate(date).some((lr) => lr.staff_id === staffId && lr.status === "approved");
  }, [getLeavesForDate]);

  // ----- Handlers -----
  const handleDayClick = (date) => {
    if (!isAdmin) return;
    setSelectedCell({ date: format(date, "yyyy-MM-dd"), branch_id: branchId });
    setSelectedStaffId("");
    setAssignmentNotes("");
    setAssignDialogOpen(true);
  };

  const handleAssignStaff = async () => {
    if (!selectedStaffId || !selectedCell) { toast.error("Please select a staff member"); return; }
    setSubmitting(true);
    try {
      await createRosterAssignment({
        date: selectedCell.date, branch_id: selectedCell.branch_id,
        staff_id: selectedStaffId, notes: assignmentNotes,
      });
      toast.success("Staff assigned");
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAssignment = async (date, staffId) => {
    try {
      await deleteRosterByDetails(date, branchId, staffId);
      toast.success("Assignment removed");
      fetchData();
    } catch {
      toast.error("Failed to remove assignment");
    }
  };

  const handleCreateLeaveRequest = async () => {
    if (!leaveForm.staff_id || !leaveForm.reason) { toast.error("Please fill in all fields"); return; }
    const start = format(leaveForm.start_date, "yyyy-MM-dd");
    const end = format(leaveForm.end_date, "yyyy-MM-dd");
    if (end < start) { toast.error("End date cannot be before start date"); return; }
    setSubmitting(true);
    try {
      await createLeaveRequest({ staff_id: leaveForm.staff_id, start_date: start, end_date: end, reason: leaveForm.reason });
      toast.success("Leave request submitted");
      setLeaveDialogOpen(false);
      setLeaveForm({ staff_id: "", start_date: new Date(), end_date: new Date(), reason: "" });
      fetchLeaveList();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit leave request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLeaveStatus = async (requestId, status) => {
    try {
      await updateLeaveRequest(requestId, { status });
      toast.success(`Leave request ${status}`);
      fetchLeaveList();
      fetchData();
    } catch {
      toast.error("Failed to update leave request");
    }
  };

  const handleDeleteLeaveRequest = async (requestId) => {
    try {
      await deleteLeaveRequest(requestId);
      toast.success("Leave request deleted");
      fetchLeaveList();
      fetchData();
    } catch {
      toast.error("Failed to delete leave request");
    }
  };

  const getBranchName = useCallback(
    (id) => branches.find((b) => b.id === id)?.name || id, [branches]
  );

  // Available staff for assign dialog (not assigned, not on leave)
  const availableStaff = useMemo(() => {
    if (!selectedCell) return staff;
    const d = new Date(selectedCell.date);
    const assignedIds = new Set(getAssignmentsForCell(d).map((a) => a.staff_id));
    return staff.filter((s) => !assignedIds.has(s.id) && !isStaffOnLeave(s.id, d));
  }, [selectedCell, staff, getAssignmentsForCell, isStaffOnLeave]);

  return (
    <div className="space-y-6 animate-in">
      <Tabs defaultValue="roster" className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList className="bg-sand" data-testid="roster-tabs">
            <TabsTrigger value="roster" data-testid="tab-roster">Monthly Roster</TabsTrigger>
            <TabsTrigger value="leave" data-testid="tab-leave">Leave Requests</TabsTrigger>
          </TabsList>
          <Button onClick={() => setLeaveDialogOpen(true)} variant="outline" data-testid="new-leave-request-btn">
            <CalendarOff className="h-4 w-4 mr-2" /> New Leave Request
          </Button>
        </div>

        <TabsContent value="roster" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-forest" data-testid="roster-month-label">
                {format(monthDate, "MMMM yyyy")}
              </h2>
              <p className="text-sage text-sm">{getBranchName(branchId)}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setMonthDate((m) => addMonths(m, -1))} data-testid="roster-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" onClick={() => setMonthDate(startOfMonth(new Date()))} data-testid="roster-today-btn">This Month</Button>
              <Button variant="outline" size="icon" onClick={() => setMonthDate((m) => addMonths(m, 1))} data-testid="roster-next-month"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>

          <RosterMonthCalendar
            loading={loading}
            monthDate={monthDate}
            days={days}
            getAssignmentsForCell={getAssignmentsForCell}
            getLeavesForDate={getActiveLeavesForDate}
            onDayClick={handleDayClick}
            onRemoveAssignment={handleRemoveAssignment}
          />
        </TabsContent>

        <TabsContent value="leave" className="space-y-6">
          <RosterShiftList
            leaveFilter={leaveFilter}
            onLeaveFilterChange={setLeaveFilter}
            leaveCustomStart={leaveCustomStart}
            onLeaveCustomStartChange={setLeaveCustomStart}
            leaveCustomEnd={leaveCustomEnd}
            onLeaveCustomEndChange={setLeaveCustomEnd}
            leaveLoading={leaveLoading}
            leaveRequests={leaveListRequests}
            isAdmin={isAdmin}
            onUpdateStatus={handleUpdateLeaveStatus}
            onDelete={handleDeleteLeaveRequest}
          />
        </TabsContent>
      </Tabs>

      <RosterShiftForm
        assignDialogOpen={assignDialogOpen}
        onAssignDialogChange={setAssignDialogOpen}
        selectedCell={selectedCell}
        getBranchName={getBranchName}
        availableStaff={availableStaff}
        selectedStaffId={selectedStaffId}
        onSelectStaffId={setSelectedStaffId}
        assignmentNotes={assignmentNotes}
        onAssignmentNotesChange={setAssignmentNotes}
        submitting={submitting}
        onAssignStaff={handleAssignStaff}
        leaveDialogOpen={leaveDialogOpen}
        onLeaveDialogChange={setLeaveDialogOpen}
        staff={staff}
        leaveForm={leaveForm}
        onLeaveFormChange={(partial) => setLeaveForm((prev) => ({ ...prev, ...partial }))}
        onCreateLeaveRequest={handleCreateLeaveRequest}
      />
    </div>
  );
}
